import { useState } from "react";
import Header from "@/atomic/atoms/Header";
import { PropsView } from "./Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";
import { trackAddPaymentInfo } from "@/analytics/ecommerce";

type PaymentMethod = "card" | "pix";

export default function CheckoutView({
  goToCard,
  goToPix,
  goBack,
  plate,
  total,
  passagesTotal,
  finesTotal,
  items = [],
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const title = themeName === "zapay" ? "Pedágio" : t("pages:checkout:payment");

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    null,
  );

  const formatCurrency = (value: number) =>
    value ? `R$ ${value.toFixed(2).replace(".", ",")}` : "R$ 0,00";

  const formattedTotal = formatCurrency(total);
  const formattedPassages = formatCurrency(passagesTotal);
  const formattedFines = formatCurrency(finesTotal);

  const formattedPlate = plate?.toUpperCase() ?? "";

  const handleConfirm = () => {
    if (!selectedMethod) return;

    trackAddPaymentInfo({
      flowType: "pagamento_avulso",
      paymentType: selectedMethod === "pix" ? "pix" : "cartao_credito",
      value: total,
    });

    if (selectedMethod === "card") goToCard();
    if (selectedMethod === "pix") goToPix();
  };

  return (
    <div className="h-full flex flex-col">
      <Header title={title} goBack={goBack} hasLeftButton />

      <main className="flex-1 overflow-auto px-4 pb-28 bg-white">
        <div className="max-w-[480px] mx-auto">
          <h2 className="text-[22px] font-bold text-text mt-6 tracking-[-0.2px] leading-[1.2]">
            {t("pages:checkout:paymentMethod")}
          </h2>
          <p className="text-sm text-text-secondary mt-2 leading-[1.3]">
            {t("pages:checkout:paymentMethodDescription")}
          </p>

          <div className="mt-5 bg-background border border-border rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:payment")}
              </p>
              <p className="text-sm text-text-secondary tracking-[-0.4px]">
                {t("common:plate")} : {formattedPlate}
              </p>
            </div>

            <div className="w-full h-px bg-border my-4" />

            <div className="flex flex-col gap-3.5">
              {items
                .filter((item) => item.fmtDataPassagem)
                .map((item) => (
                  <p
                    key={item.id}
                    className="text-sm text-text-secondary tracking-[-0.4px]"
                  >
                    {item.fmtDataPassagem}
                  </p>
                ))}
              <div className="flex justify-between items-center">
                <p className="text-sm text-text-secondary tracking-[-0.4px]">
                  {t("pages:checkout:passages")}
                </p>
                <p className="text-sm font-medium text-text tracking-[-0.4px]">
                  {formattedPassages}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-text-secondary tracking-[-0.4px]">
                  {t("pages:checkout:fines")}
                </p>
                <p className="text-sm font-medium text-text tracking-[-0.4px]">
                  {formattedFines}
                </p>
              </div>
            </div>

            <div className="w-full h-px bg-border my-4" />

            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:total")}
              </p>
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {formattedTotal}
              </p>
            </div>
          </div>

          <div className="mt-4 bg-white border border-border rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:paymentMethods")}
              </p>
              <img
                src="/svg/icon-arrow-up-2.0.svg"
                width={20}
                height={20}
                alt=""
                className="opacity-60"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4">
              <button
                type="button"
                id="checkout-select-card"
                data-testid="checkout-select-card"
                data-ga-event="checkout_select_payment_method_card"
                data-ga-payment-method="card"
                onClick={() => setSelectedMethod("card")}
                className="flex items-center justify-between w-full text-start"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/svg/icon-card-2.0.svg"
                    width={24}
                    height={24}
                    alt=""
                  />
                  <p className="text-sm font-medium text-text tracking-[-0.4px]">
                    {t("pages:checkout:creditCard")}
                  </p>
                </div>
                <span
                  className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  {selectedMethod === "card" && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </span>
              </button>

              <div className="w-full h-px bg-border" />

              <button
                type="button"
                id="checkout-select-pix"
                data-testid="checkout-select-pix"
                data-ga-event="checkout_select_payment_method_pix"
                data-ga-payment-method="pix"
                onClick={() => setSelectedMethod("pix")}
                className="flex items-center justify-between w-full text-start"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/svg/icon-pix-2.0.svg"
                    width={24}
                    height={24}
                    alt=""
                  />
                  <p className="text-sm font-medium text-text tracking-[-0.4px]">
                    {t("pages:checkout:pix")}
                  </p>
                </div>
                <span
                  className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  {selectedMethod === "pix" && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 left-0 right-0 px-5 py-2.5 bg-white">
        <div className="max-w-[480px] mx-auto">
          <button
            type="button"
            id="checkout-confirm-payment"
            data-testid="checkout-confirm-payment"
            data-ga-event={
              selectedMethod
                ? `checkout_confirm_payment_${selectedMethod}`
                : "checkout_confirm_payment"
            }
            data-ga-payment-method={selectedMethod ?? ""}
            onClick={handleConfirm}
            disabled={!selectedMethod}
            className={`w-full h-[60px] rounded-theme-large text-base font-medium text-white transition-opacity ${
              selectedMethod
                ? "bg-primary cursor-pointer"
                : "bg-disabled text-disabled-text cursor-not-allowed"
            }`}
          >
            {t("pages:checkout:confirmPayment")}
          </button>
        </div>
      </div>
    </div>
  );
}
